import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { FONTS } from '../constants/Fonts';
import { IMAGE_CONSTANTS } from '../constants/ImageConstans';
import { TEXT_LABELS } from '../constants/TextLabelConstants';
import { hasTournamentPrizePlayerDetails } from '../utils/utils';
import './TournamentWinnerPrizesSidebar.css';

const RANK_SUFFIX = {
  1: TEXT_LABELS.RAP_FIRE_WINNER_RANK_POSTFIX_ST,
  2: TEXT_LABELS.RAP_FIRE_WINNER_RANK_POSTFIX_ND,
  3: TEXT_LABELS.RAP_FIRE_WINNER_RANK_POSTFIX_RD,
};

const prizeRank = (item) => Number(item?.rank ?? item?.rankFrom) || 0;

const rankPlace = (rank) => {
  const n = Number(rank) || 0;
  const suffix =
    n % 100 >= 11 && n % 100 <= 13
      ? TEXT_LABELS.RAP_FIRE_WINNER_RANK_POSTFIX_TH
      : RANK_SUFFIX[n % 10] || TEXT_LABELS.RAP_FIRE_WINNER_RANK_POSTFIX_TH;
  return `${n}${suffix}`;
};

const CroppedAsset = ({ className, src, alt = '' }) => (
  <span className={className} aria-hidden={alt ? undefined : 'true'}>
    <img src={src} alt={alt} />
  </span>
);

CroppedAsset.propTypes = {
  className: PropTypes.string.isRequired,
  src: PropTypes.string.isRequired,
  alt: PropTypes.string,
};

/**
 * Figma 20804:16394 — completed-tournament winner prizes slider.
 * Prize rows come from tournamentRewardAllotmentArray.
 */
const TournamentWinnerPrizesSidebar = ({
  prizes = [],
  heading = TEXT_LABELS.TOURNAMENT_WINNER_PRIZES_EYEBROW,
  title = TEXT_LABELS.TOURNAMENT_WINNER_PRIZES_TITLE,
  subtitle = TEXT_LABELS.TOURNAMENT_WINNER_PRIZES_SUBTITLE,
  autoPlay = false,
  intervalMs = 4000,
}) => {
  const slides = Array.isArray(prizes)
    ? prizes.filter(
        (item) => prizeRank(item) > 0 && hasTournamentPrizePlayerDetails(item)
      )
    : [];
  const [index, setIndex] = useState(0);

  useEffect(() => {
    setIndex(0);
  }, [slides.length]);

  useEffect(() => {
    if (!autoPlay || slides.length < 2) return undefined;
    const timer = setInterval(() => {
      setIndex((prev) => (prev + 1) % slides.length);
    }, intervalMs);
    return () => clearInterval(timer);
  }, [autoPlay, slides.length, intervalMs]);

  if (!slides.length) return null;

  const safeIndex = Math.min(index, slides.length - 1);
  const slide = slides[safeIndex] || slides[0];
  const slideRank = prizeRank(slide);
  const place = rankPlace(slideRank);
  const prizeIcon = slide.prizeIcon || IMAGE_CONSTANTS.V2_TOURNAMENT_PRIZE_TROPHY;
  const isTrophyFallback = prizeIcon === IMAGE_CONSTANTS.V2_TOURNAMENT_PRIZE_TROPHY;
  const avatar = slide.avatar || IMAGE_CONSTANTS.V2_IMAGE_PLACEHOLDER;
  const username = slide.username || '—';
  const ribbonLabel = TEXT_LABELS.TOURNAMENT_WINNER_PRIZES_RIBBON.replace(
    '{place}',
    place
  );
  const placeLabel = TEXT_LABELS.TOURNAMENT_WINNER_PRIZES_PLACE.replace(
    '{rank}',
    String(slideRank)
  );

  const goTo = (next) => {
    if (slides.length < 2) return;
    setIndex((next + slides.length) % slides.length);
  };

  return (
    <aside
      className="lb-prizes"
      data-node-id="20804:16394"
      data-name="Promo Sidebar"
      aria-label={TEXT_LABELS.TOURNAMENT_WINNER_PRIZES_TITLE}
    >
      <div className="lb-prizes__box-trophy" data-node-id="20804:17766" aria-hidden="true">
        <img
          src={IMAGE_CONSTANTS.V2_TOURNAMENT_PRIZE_TROPHY}
          alt=""
          width={148}
          height={150}
        />
      </div>

      <div className="lb-prizes__header">
        <p className="lb-prizes__eyebrow" style={{ fontFamily: FONTS.INTER_BOLD }}>
          {heading || TEXT_LABELS.TOURNAMENT_WINNER_PRIZES_EYEBROW}
        </p>
        <h2
          className="lb-prizes__title"
          style={{ fontFamily: FONTS.INTER_EXTRABOLD }}
        >
          {title || TEXT_LABELS.TOURNAMENT_WINNER_PRIZES_TITLE}
        </h2>
        <p className="lb-prizes__subtitle" style={{ fontFamily: FONTS.INTER_MEDIUM }}>
          {subtitle || TEXT_LABELS.TOURNAMENT_WINNER_PRIZES_SUBTITLE}
        </p>
      </div>

      <div className="lb-prizes__tabs" role="tablist" aria-label="Prize ranks">
        {slides.map((item, slideIndex) => {
          const active = slideIndex === safeIndex;
          return (
            <button
              key={`prize-tab-${slideIndex}-${prizeRank(item)}`}
              type="button"
              role="tab"
              aria-selected={active}
              className={`lb-prizes__tab${active ? ' is-active' : ''}`}
              style={{ fontFamily: FONTS.INTER_SEMIBOLD }}
              onClick={() => setIndex(slideIndex)}
            >
              <span className="lb-prizes__tab-label">{rankPlace(prizeRank(item))}</span>
            </button>
          );
        })}
      </div>

      <div className="lb-prizes__stage">
        {slides.length > 1 ? (
          <button
            type="button"
            className="lb-prizes__arrow is-prev"
            onClick={() => goTo(safeIndex - 1)}
            aria-label="Previous prize"
          >
            <CroppedAsset
              className="lb-prizes__arrow-ic"
              src={IMAGE_CONSTANTS.V2_TOURNAMENT_PRIZE_ARROW}
            />
          </button>
        ) : null}

        <div className="lb-prizes__card-wrap">
          <div className="lb-prizes__stars" aria-hidden="true">
            <CroppedAsset
              className="lb-prizes__star lb-prizes__star--side is-left"
              src={IMAGE_CONSTANTS.V2_TOURNAMENT_PRIZE_STAR}
            />
            <CroppedAsset
              className="lb-prizes__star lb-prizes__star--center"
              src={IMAGE_CONSTANTS.V2_TOURNAMENT_PRIZE_STAR}
            />
            <CroppedAsset
              className="lb-prizes__star lb-prizes__star--side is-right"
              src={IMAGE_CONSTANTS.V2_TOURNAMENT_PRIZE_STAR}
            />
          </div>

          <div className="lb-prizes__card">
            <div className="lb-prizes__card-inner">
              <img
                className="lb-prizes__burst"
                src={IMAGE_CONSTANTS.V2_TOURNAMENT_PRIZE_BURST}
                alt=""
              />
            </div>
          </div>

          <div className="lb-prizes__ribbon">
            <img
              className="lb-prizes__ribbon-img"
              src={IMAGE_CONSTANTS.V2_TOURNAMENT_PRIZE_RIBBON}
              alt=""
            />
            <p
              className="lb-prizes__ribbon-text"
              style={{ fontFamily: FONTS.INTER_EXTRABOLD }}
            >
              {ribbonLabel}
            </p>
          </div>

          <p
            className="lb-prizes__gold-title"
            style={{ fontFamily: FONTS.INTER_EXTRABOLD }}
          >
            {TEXT_LABELS.TOURNAMENT_WINNER_PRIZES_CHAMPION}
          </p>

          <div className="lb-prizes__avatar-wrap">
            <div className="lb-prizes__avatar">
              <img src={avatar} alt="" />
            </div>
            <img
              className="lb-prizes__laurel is-left"
              src={IMAGE_CONSTANTS.V2_TOURNAMENT_PRIZE_LAUREL_LEFT}
              alt=""
            />
            <img
              className="lb-prizes__laurel is-right"
              src={IMAGE_CONSTANTS.V2_TOURNAMENT_PRIZE_LAUREL_RIGHT}
              alt=""
            />
          </div>

          <p className="lb-prizes__name" style={{ fontFamily: FONTS.INTER_MEDIUM }}>
            {username}
          </p>
          <p className="lb-prizes__place" style={{ fontFamily: FONTS.INTER_MEDIUM }}>
            {placeLabel}
          </p>

          <div className="lb-prizes__ticket">
            <img
              className="lb-prizes__ticket-bg"
              src={IMAGE_CONSTANTS.V2_TOURNAMENT_PRIZE_TICKET}
              alt=""
            />
            <div className="lb-prizes__ticket-row">
              <span
                className={`lb-prizes__prize-ic${
                  isTrophyFallback ? ' is-trophy' : ''
                }`}
              >
                <img
                  src={prizeIcon}
                  alt=""
                  onError={(event) => {
                    const img = event.currentTarget;
                    if (img.dataset.trophyFallback === '1') return;
                    img.dataset.trophyFallback = '1';
                    img.src = IMAGE_CONSTANTS.V2_TOURNAMENT_PRIZE_TROPHY;
                    img.parentElement?.classList.add('is-trophy');
                  }}
                />
              </span>
              {slide.prizeTitle ? (
                <p
                  className="lb-prizes__prize-title"
                  style={{ fontFamily: FONTS.INTER_EXTRABOLD }}
                >
                  {slide.prizeTitle}
                </p>
              ) : null}
            </div>
          </div>
        </div>

        {slides.length > 1 ? (
          <button
            type="button"
            className="lb-prizes__arrow is-next"
            onClick={() => goTo(safeIndex + 1)}
            aria-label="Next prize"
          >
            <CroppedAsset
              className="lb-prizes__arrow-ic"
              src={IMAGE_CONSTANTS.V2_TOURNAMENT_PRIZE_ARROW}
            />
          </button>
        ) : null}
      </div>
    </aside>
  );
};

TournamentWinnerPrizesSidebar.propTypes = {
  prizes: PropTypes.arrayOf(
    PropTypes.shape({
      rank: PropTypes.number,
      rankFrom: PropTypes.number,
      rankTo: PropTypes.number,
      prizeTitle: PropTypes.string,
      prizeIcon: PropTypes.string,
      userId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      username: PropTypes.string,
      avatar: PropTypes.string,
    })
  ),
  autoPlay: PropTypes.bool,
  heading: PropTypes.string,
  title: PropTypes.string,
  subtitle: PropTypes.string,
  intervalMs: PropTypes.number,
};

export default TournamentWinnerPrizesSidebar;
